import { useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import { useParams } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { RESOURCES } from '@shared/Resources/types';
import { MfaStatus } from '@shared/constants/mfa';
import { getResources } from '@containers/Admin/shared/ResourcesWrapper/actions';
import { userInformationFields } from './helpers.tsx';
import {
  AdminActionItem,
  AdminActionsRow,
  FieldsGrid,
  FieldSlot,
  InformationSection,
  InformationTitle,
  InformationWrapper,
  LoginStatus,
  SectionTitle,
  SummaryGrid,
} from './styles';
import { fetchUserPage } from '../../../actions';
import { ItemWithLabel } from './components/ItemWithLabel/index.tsx';
import { SelectMentors } from './components/SelectMentors/index.tsx';
import { SelectCourse } from './components/SelectCourse/index.tsx';
import { SelectBusinessUnits } from './components/SelectBusinessUnits/index.tsx';
import { SelectStatus } from './components/SelectStatus/index.tsx';
import { ItemFields } from './types.ts';
import { useTranslation } from 'react-i18next';
import ResetPasswordButton from './components/ResetPasswordButton';
import UpdateMfaButton from './components/UpdateMfaButton';
import { EditPhoneNumber } from './components/EditPhoneNumber';
import { SelectLanguage } from './components/SelectLanguage';
import { ExperimentalFeatures } from './components/ExperimentalFeatures.tsx';
import { EditUserName } from './components/EditUserName/index.tsx';
import { EditBirthdate } from './components/EditBirthdate/index.tsx';
import { getMessagingDate } from '@utils/timeHelpers';
/**
 * User Information Page
 */
export const UserInformation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { userData } = useSelector((state: RootStateOrAny) => state.get('userPage').toJS()); // FIXME replace to typing store
  const { roles } = useSelector((state: RootStateOrAny) => state.getIn(['app', 'authentication', 'user']).toJS()); // FIXME replace to typing store
  const isAdmin = roles.includes('30') || roles.includes('40') || roles.includes('41');
  const isTechAdmin = roles.includes('40');
  const isMentor = roles.includes('10') || roles.includes('11') || roles.includes('20') || roles.includes('21') || roles.includes('22');

  useEffect(() => {
    if (isAdmin) {
      dispatch(getResources(RESOURCES.COURSE, '', '', false));
    }
    dispatch(fetchUserPage(id));
  }, [dispatch, id, isAdmin]);

  const renderFields = (fields: Array<ItemFields>, fieldIds: Array<string>, wideFields: Array<string> = []) => fields
    .map((field) => ({ field, order: fieldIds.indexOf(field.id) }))
    .filter(({ order }) => order >= 0)
    .sort((left, right) => left.order - right.order)
    .map(({ field }) => field)
    .map(({ id: fieldId, label, content }) => (
      <FieldSlot key={fieldId} className={`${fieldId} ${wideFields.includes(fieldId) ? 'wide' : ''}`}>
        <ItemWithLabel
          // TODO: Use fieldId for every row after legacy field controls no longer rely on remounting.
          key={['phone', 'userName', 'birthdate'].includes(fieldId) ? fieldId : nanoid()}
          label={label}
        >
          {content}
        </ItemWithLabel>
      </FieldSlot>
    ));

  const fields = userData ? userInformationFields(
    userData,
    <SelectMentors isMentor={isMentor} />,
    <SelectCourse isMentor={isMentor} />,
    <SelectBusinessUnits canEditBusinessUnits={isTechAdmin} />,
    <SelectStatus isMentor={isMentor} />,
    <SelectLanguage isMentor={isMentor} />,
    isAdmin ? <EditPhoneNumber /> : userData.phone || '',
    isAdmin ? <EditUserName /> : userData.fullName || '',
    isAdmin ? <EditBirthdate /> : userData.birthdate ? getMessagingDate(userData.birthdate) : '',
  ) : [];
  const hasMfa = userData?.mfaStatus === MfaStatus.SMS || userData?.mfaStatus === MfaStatus.EMAIL;
  const MfaIcon = hasMfa ? CheckIcon : RemoveCircleOutlineIcon;
  const mfaStatusLabel = userData?.mfaStatus === MfaStatus.SMS
    ? t('Authentication.MFA.sms')
    : userData?.mfaStatus === MfaStatus.EMAIL
      ? t('Authentication.MFA.email')
      : t('Authentication.MFA.inactive');
  const hasPassword = Boolean(userData?.isMigratedToCognito);
  const PasswordIcon = hasPassword ? CheckIcon : RemoveCircleOutlineIcon;
  const passwordStatusTitle = hasPassword
    ? t('Authentication.Password.passwordSet')
    : t('Authentication.MFA.inactive');

  return (
    <>
      <InformationTitle>{t('UI.userInformation' as any)}</InformationTitle>
      <InformationWrapper>
        {userData ? (
          <>
            <SummaryGrid>
              {renderFields(fields, ['email', 'startDate', 'age', 'state', 'country'])}
            </SummaryGrid>
            <InformationSection className="profile">
              <SectionTitle>{t('Informative.Sections.profile')}</SectionTitle>
              <FieldsGrid className="profile">
                {renderFields(fields, ['status', 'userName', 'birthdate', 'language'], ['userName'])}
              </FieldsGrid>
            </InformationSection>
            <InformationSection className="program">
              <SectionTitle>{t('Informative.Sections.program')}</SectionTitle>
              <FieldsGrid className="program">
                {renderFields(fields, ['businessUnits', 'course', 'mentor'], ['businessUnits'])}
              </FieldsGrid>
            </InformationSection>
            <InformationSection className="login">
              <SectionTitle>{t('Informative.Sections.login')}</SectionTitle>
              <FieldsGrid className="login">
                {renderFields(fields, ['phone'], ['phone'])}
              </FieldsGrid>
            {isAdmin && (
                <AdminActionsRow>
                  <AdminActionItem>
                    <ItemWithLabel label={<span>{t('Authentication.Password.password')}</span>}>
                      <LoginStatus className={hasPassword ? 'password-set' : 'not-set'} title={passwordStatusTitle}>
                        <PasswordIcon />
                        {!hasPassword && t('Authentication.MFA.inactive')}
                      </LoginStatus>
                      <ResetPasswordButton userId={userData.id} userEmail={userData.email || userData.userName} />
                    </ItemWithLabel>
                  </AdminActionItem>
                  <AdminActionItem>
                    <ItemWithLabel label={<span>{t('Informative.Labels.mfa')}</span>}>
                      <LoginStatus className={hasMfa ? 'mfa-set' : 'not-set'}>
                        <MfaIcon />
                        {mfaStatusLabel}
                      </LoginStatus>
                      <UpdateMfaButton
                        userId={userData.id}
                        userEmail={userData.email || userData.userName}
                        phone={userData.phone}
                      />
                    </ItemWithLabel>
                  </AdminActionItem>
                </AdminActionsRow>
            )}
            </InformationSection>
            <InformationSection className="advanced">
              <SectionTitle>{t('Informative.Sections.advanced')}</SectionTitle>
              <FieldsGrid className="advanced">
                <ExperimentalFeatures />
              </FieldsGrid>
            </InformationSection>
          </>
        ) : (
          t('UI.noInformation' as any)
        )}
      </InformationWrapper>
    </>
  );
};
